/**
 * DataMappingUtils Module
 *
 * Utility functions for mapping database data to payslip cell values.
 * Converts raw database field names to configuration valueIds for display in payslip cells.
 *
 * @module DataMappingUtils
 */

/**
 * Type definitions for payslip data structures.
 * These types are defined locally since they don't exist in EmployeeService.
 */

/**
 * Payment data interface.
 * @interface
 */
interface PaymentData {
  [key: string]: any;
}

/**
 * Deduction data interface.
 * @interface
 */
interface DeductionData {
  [key: string]: any;
}

/**
 * Attendance data interface.
 * @interface
 */
interface AttendanceData {
  [key: string]: any;
}

/**
 * Bonus payment data interface.
 * @interface
 */
interface BonusPaymentData {
  [key: string]: any;
}

/**
 * Bonus deduction data interface.
 * @interface
 */
interface BonusDeductionData {
  [key: string]: any;
}

/**
 * Bonus attendance data interface.
 * @interface
 */
interface BonusAttendanceData {
  [key: string]: any;
}

/**
 * Salary notes data interface.
 * @interface
 */
interface SalaryNotesData {
  [key: string]: any;
}

/**
 * Bonus notes data interface.
 * @interface
 */
interface BonusNotesData {
  [key: string]: any;
}

/**
 * Maps payment data to the corresponding valueId for display in payslip cells.
 *
 * Converts database field names (e.g., baseSalary) to configuration valueIds (e.g., p_v1).
 * Also calculates derived values like taxable payment totals.
 *
 * @param paymentData - The payment data fetched from the database
 * @returns Object mapping valueId to actual values (numbers or strings)
 *
 * @example
 * ```typescript
 * const paymentData = { baseSalary: 300000, positionAllowance: 50000 };
 * const mappings = mapPaymentDataToCells(paymentData);
 * // Returns: { p_v1: 300000, p_v2: 50000, ... }
 * ```
 */
export const mapPaymentDataToCells = (paymentData: PaymentData | null): Record<string, number | string> => {
  if (!paymentData) return {};
  
  return {
    'p_v1': paymentData.baseSalary || 0,           // 基本給
    'p_v2': paymentData.positionAllowance || 0,     // 役職手当
    'p_v3': paymentData.familyAllowance || 0,       // 家族手当
    'p_v4': paymentData.housingAllowance || 0,      // 住宅手当
    'p_v5': paymentData.qualificationAllowance || 0, // 資格手当
    'p_v6': paymentData.diligenceAllowance || 0,    // 精勤手当
    'p_v7': paymentData.fieldAllowance || 0,         // 現場手当
    'p_v8': paymentData.overtimeAllowance || 0,     // 時間外手当
    'p_v9': paymentData.otherAllowance || 0,        // その他手当
    'p_v10': paymentData.yearEndAdjustment || 0,    // 年末調整額
    'p_v11': paymentData.longServiceAward || 0,     // 永年勤続表彰
    'p_v12': paymentData.totalOtherAllowances || 0,       // 他手当計
    'p_v13': paymentData.regularOvertime || 0,      // 普通残業
    'p_v14': paymentData.nightOvertime || 0,        // 深夜残業
    'p_v15': paymentData.holidayOvertime || 0,      // 休出残業
    'p_v16': paymentData.holidayNight || 0,         // 休出深夜
    'p_v17': paymentData.legalHolidayOvertimeAmount || 0, // 法休残業
    'p_v18': paymentData.legalHolidayNightAmount || 0,    // 法休深夜
    'p_v19': paymentData.legalOvertimeAmount || 0,        // 法内残業
    'p_v20': paymentData.absenceDeduction || 0,     // 欠勤控除
    'p_v21': paymentData.lateEarlyDeduction || 0,   // 遅早控除
    'p_v22': paymentData.taxableCommutingExpense || 0,       // 課税通勤費
    'p_v23': paymentData.nonTaxableCommutingExpense || 0,    // 非課税通勤費
    'p_v24': paymentData.taxableMealCost || 0,          // 課税食事代
    'p_v25': paymentData.nonTaxableMealCost || 0,       // 非課税食事代
    'p_v26': paymentData.taxableNightDuty || 0,     // 課税宿直
    'p_v27': paymentData.nonTaxableNightDuty || 0,    // 非課税宿直
    'p_v28': paymentData.taxableSalaryIncrease || 0,         // 課税昇給額
    'p_v29': paymentData.nonTaxableSalaryIncrease || 0,      // 非課税昇給額
    // 課税支給額 (Taxable Payment) - Sum of all taxable payments minus deductions
    // Calculated from AB列～BC列, excluding non-taxable payments and year-end adjustment
    'p_v30': (paymentData.baseSalary || 0) + (paymentData.positionAllowance || 0) + (paymentData.familyAllowance || 0) + (paymentData.housingAllowance || 0) + (paymentData.qualificationAllowance || 0) + (paymentData.diligenceAllowance || 0) + (paymentData.fieldAllowance || 0) + (paymentData.overtimeAllowance || 0) + (paymentData.otherAllowance || 0) + (paymentData.longServiceAward || 0) + (paymentData.taxableCommutingExpense || 0) + (paymentData.taxableMealCost || 0) + (paymentData.taxableNightDuty || 0) + (paymentData.taxableSalaryIncrease || 0) + (paymentData.regularOvertime || 0) + (paymentData.nightOvertime || 0) + (paymentData.holidayOvertime || 0) + (paymentData.holidayNight || 0) + (paymentData.legalHolidayOvertimeAmount || 0) + (paymentData.legalHolidayNightAmount || 0) + (paymentData.legalOvertimeAmount || 0) - (paymentData.absenceDeduction || 0) - (paymentData.lateEarlyDeduction || 0),
    // 非課支給額 (Non-taxable Payment) - Sum of all non-taxable payments
    'p_v31': (paymentData.nonTaxableCommutingExpense || 0) + (paymentData.nonTaxableMealCost || 0) + (paymentData.nonTaxableNightDuty || 0) + (paymentData.nonTaxableSalaryIncrease || 0),
    'p_v32': paymentData.taxablePaymentCumulative || 0, // 課税支給累計
    'p_v33': paymentData.totalPayment || 0,   // 支給合計 (from database)
    
    // Personal messages (個人メッセージ)
    'pm_v1': paymentData.personalMessage1 || '',    // 個人メッセージ1
    'pm_v2': paymentData.personalMessage2 || ''     // 個人メッセージ2
  };
};

/**
 * Maps deduction data to the corresponding valueId for display in payslip cells.
 *
 * Converts database field names to configuration valueIds for deduction section.
 * Also calculates derived values like social insurance totals.
 *
 * @param deductionData - The deduction data fetched from the database
 * @returns Object mapping valueId to actual values (numbers or strings)
 */
export const mapDeductionDataToCells = (deductionData: DeductionData | null): Record<string, number | string> => {
  if (!deductionData) return {};
  
  
  return {
    'd_v1': deductionData.healthInsurance || 0,        // 健康保険
    'd_v2': deductionData.longTermCareInsurance || 0,       // 介護保険
    'd_v3': deductionData.pensionInsurance || 0,       // 厚生年金
    'd_v4': deductionData.welfareFund || 0,            // 厚生基金
    'd_v5': deductionData.employmentInsurance || 0,    // 雇用保険
    // 社保合計 (Social Insurance Total) - Sum of all social insurance deductions
    'd_v6': (deductionData.healthInsurance || 0) + (deductionData.longTermCareInsurance || 0) + (deductionData.pensionInsurance || 0) + (deductionData.welfareFund || 0) + (deductionData.employmentInsurance || 0),
    'd_v7': deductionData.taxableAmount || 0,          // 課税対象額
    'd_v8': deductionData.incomeTax || 0,              // 所得税
    'd_v9': deductionData.residenceTax || 0,           // 住民税
    'd_v10': deductionData.residenceTaxCorrection || 0, // 住民税訂正
    'd_v11': deductionData.employeeSavings || 0,               // 財形貯蓄
    'd_v12': deductionData.rent || 0,                  // 家賃
    'd_v13': deductionData.socialInsuranceDeductionCumulative || 0, // 社保控除累計
    'd_v14': deductionData.incomeTaxCumulative || 0,               // 所得税累計
    'd_v15': deductionData.totalDeductions || 0,  // 控除合計
  };
};

/**
 * Maps attendance data to the corresponding valueId for display in payslip cells.
 *
 * Converts database field names to configuration valueIds for attendance section.
 *
 * @param attendanceData - The attendance data fetched from the database
 * @returns Object mapping valueId to actual values (numbers or strings)
 */
export const mapAttendanceDataToCells = (attendanceData: AttendanceData | null): Record<string, number | string> => {
  if (!attendanceData) return {};
  
  return {
    'a_v1': attendanceData.workingDays || 0,               // 就業日数
    'a_v2': attendanceData.attendanceDays || 0,         // 出勤日数
    'a_v3': attendanceData.absenceDays || 0,            // 欠勤日数
    'a_v4': attendanceData.paidLeaveDays || 0,          // 有休日数
    'a_v5': attendanceData.specialLeaveDays || 0,       // 特休日数
    'a_v6': attendanceData.holidayWorkDays || 0,        // 休出日数
    'a_v7': attendanceData.legalHolidayWorkDays || 0,   // 法内休出日数
    'a_v8': attendanceData.compensatoryLeaveDays || 0,  // 代休日数
    'a_v9': attendanceData.mealCount || 0,              // 食事回数
    'a_v10': attendanceData.nightDutyCount || 0,         // 宿直回数
    'a_v11': attendanceData.lateEarlyCount || 0,         // 遅早回数
    'a_v12': attendanceData.attendanceHours || '',              // 出勤時間 (Working hours - time string format)
    'a_v13': attendanceData.lateEarlyHours || 0,         // 遅早時間
    'a_v14': attendanceData.regularOvertimeHours || 0,   // 普通残業時間
    'a_v15': attendanceData.nightOvertimeHours || 0,     // 深夜残業時間
    'a_v16': attendanceData.holidayOvertimeHours || 0,   // 休出残業時間
    'a_v17': attendanceData.holidayNightHours || 0,      // 休出深夜時間
    'a_v18': attendanceData.legalHolidayOvertimeHours || 0, // 法休残業時間
    'a_v19': attendanceData.legalHolidayNightHours || 0,    // 法休深夜時間
    'a_v20': attendanceData.legalOvertimeHours || 0,     // 法内残業時間
    'a_v21': attendanceData.dependentsCount || 0,        // 扶養等の数
    'a_v22': attendanceData.remainingPaidLeave || 0,     // 有休残日数
  };
};

/**
 * Maps bonus payment data to the corresponding valueId for display in payslip cells.
 *
 * Converts database field names to configuration valueIds for bonus payment section.
 * Also maps critical totals (total payment, taxable cumulative) that PayslipTable expects.
 *
 * @param bonusPaymentData - The bonus payment data fetched from the database
 * @returns Object mapping valueId to actual values (numbers or strings)
 */
export const mapBonusPaymentDataToCells = (bonusPaymentData: BonusPaymentData | null): Record<string, number | string> => {
  if (!bonusPaymentData) return {};
  
  
  // Use the actual field names from the database
  const result = {
    'bp_v1': bonusPaymentData.bonus || 0,                    // 賞与
    'bp_v2': bonusPaymentData.performanceBonus || 0,          // 業績賞与
    'bp_v3': bonusPaymentData.executiveBonus || 0,            // 役員賞与
    'bp_v4': bonusPaymentData.incentiveMoney || 0,            // 報奨金
    'bp_v5': bonusPaymentData.otherAllowancesTotal || 0,      // 他手当計
    // 課税支給額 (Taxable Payment) - Sum of G列～J列 (bonus, performanceBonus, executiveBonus, incentiveMoney)
    'bp_v6': (bonusPaymentData.bonus || 0) + (bonusPaymentData.performanceBonus || 0) + (bonusPaymentData.executiveBonus || 0) + (bonusPaymentData.incentiveMoney || 0),
    'bp_v7': 0,         // 非課支給額 (Non-taxable Payment - not applicable for bonus)
    
    // Critical totals that PayslipTable expects (mapped to payment section valueIds for compatibility)
    'p_v33': bonusPaymentData.totalPayment || 0,              // 支給合計 (Total Payment)
    'p_v32': bonusPaymentData.taxablePaymentCumulative || 0,  // 課税支給累計 (Taxable Payment Cumulative)
    
    // Personal messages
    'pm_v1': bonusPaymentData.personalMessage1 || '',         // 個人ﾒｯｾｰｼﾞ1
    'pm_v2': bonusPaymentData.personalMessage2 || ''          // 個人ﾒｯｾｰｼﾞ2
  };
  
  return result;
};

/**
 * Maps bonus deduction data to the corresponding valueId for display in payslip cells.
 *
 * Converts database field names to configuration valueIds for bonus deduction section.
 * Also maps critical totals that PayslipTable expects.
 *
 * @param bonusDeductionData - The bonus deduction data fetched from the database
 * @returns Object mapping valueId to actual values (numbers or strings)
 */
export const mapBonusDeductionDataToCells = (bonusDeductionData: BonusDeductionData | null): Record<string, number | string> => {
  if (!bonusDeductionData) return {};
  
  
  // Use the actual field names from the database
  const result = {
    'bd_v1': bonusDeductionData.healthInsurance || 0,           // 健康保険
    'bd_v2': bonusDeductionData.longTermCareInsurance || 0,     // 介護保険
    'bd_v3': bonusDeductionData.pensionInsurance || 0,          // 厚生年金
    'bd_v4': bonusDeductionData.welfareFund || 0,               // 厚生基金
    'bd_v5': bonusDeductionData.employmentInsurance || 0,       // 雇用保険
    'bd_v6': bonusDeductionData.incomeTax || 0,                 // 所得税
    'bd_v7': bonusDeductionData.travelSavings || 0,             // 旅行積立
    'bd_v8': bonusDeductionData.employeeSavings || 0,           // 財形貯蓄
    'bd_v9': bonusDeductionData.unionDues || 0,                 // 組合費
    'bd_v10': bonusDeductionData.otherDeductionsTotal || 0,     // 他控除計
    // 社保合計 (Social Insurance Total) - Sum of M列～Q列 (health, long-term care, pension, welfare fund, employment insurance)
    'bd_v11': (bonusDeductionData.healthInsurance || 0) + (bonusDeductionData.longTermCareInsurance || 0) + (bonusDeductionData.pensionInsurance || 0) + (bonusDeductionData.welfareFund || 0) + (bonusDeductionData.employmentInsurance || 0),
    
    // Critical totals that PayslipTable expects (mapped to deduction section valueIds for compatibility)
    'd_v15': bonusDeductionData.totalDeductions || 0,           // 控除合計 (Total Deductions)
    'd_v13': bonusDeductionData.socialInsuranceDeductionCumulative || 0, // 社保控除累計 (Social Insurance Deduction Cumulative)
    'd_v14': bonusDeductionData.incomeTaxCumulative || 0,       // 所得税累計 (Income Tax Cumulative)
  };
  
  return result;
};

/**
 * Maps bonus attendance data to the corresponding valueId for display in payslip cells.
 *
 * Converts database field names to configuration valueIds for bonus attendance section.
 *
 * @param bonusAttendanceData - The bonus attendance data fetched from the database
 * @returns Object mapping valueId to actual values (numbers or strings)
 */
export const mapBonusAttendanceDataToCells = (bonusAttendanceData: BonusAttendanceData | null): Record<string, number | string> => {
  if (!bonusAttendanceData) return {};
  
  
  // Use the actual field names from the database
  const result = {
    'ba_v1': bonusAttendanceData.numberOfDependentsEtc || 0,  // 扶養の数
  };
  
  return result;
};

/**
 * Maps salary notes data to the corresponding valueId for display in payslip cells.
 *
 * Converts database field names to configuration valueIds for salary notes section.
 *
 * @param salaryNotesData - The salary notes data fetched from the database
 * @returns Object mapping valueId to actual values (numbers or strings)
 */
export const mapSalaryNotesDataToCells = (salaryNotesData: SalaryNotesData | null): Record<string, number | string> => {
  if (!salaryNotesData) return {};
  
  return {
    'sn_v1': salaryNotesData.actualPayment || 0,          // 実支給額
    'sn_v2': salaryNotesData.bankTransfer1 || 0,          // 振込金額１
    'sn_v3': salaryNotesData.bankTransfer2 || 0,          // 振込金額２
    'sn_v4': salaryNotesData.bankTransfer3 || 0,          // 振込金額３
    'sn_v5': salaryNotesData.cashPayment || 0,            // 現金支給額
    'sn_v6': salaryNotesData.inKindPayment || 0,          // 現物支給額
    'sn_v7': salaryNotesData.yearEndAdjustment || 0,      // 定額減税 (Fixed Tax Reduction - using year end adjustment)
    'sn_v8': salaryNotesData.taxableAmount || 0,          // 課税対象額
    'sn_v9': salaryNotesData.deductedPayment || 0,        // 差引支給額
  };
};

/**
 * Maps bonus notes data to the corresponding valueId for display in payslip cells.
 *
 * Converts database field names to configuration valueIds for bonus notes section.
 *
 * @param bonusNotesData - The bonus notes data fetched from the database
 * @returns Object mapping valueId to actual values (numbers or strings)
 */
export const mapBonusNotesDataToCells = (bonusNotesData: BonusNotesData | null): Record<string, number | string> => {
  if (!bonusNotesData) return {};
  
  
  // Use the actual field names from the database
  const result = {
    'bn_v1': bonusNotesData.actualPaymentAmount || 0,    // 実支給額
    'bn_v2': bonusNotesData.transferAmount1 || 0,        // 振込金額１
    'bn_v3': bonusNotesData.transferAmount2 || 0,        // 振込金額２
    'bn_v4': bonusNotesData.transferAmount3 || 0,        // 振込金額３
    'bn_v5': bonusNotesData.cashPaymentAmount || 0,      // 現金支給額
    'bn_v6': bonusNotesData.inKindPaymentAmount || 0,    // 現物支給額
    'bn_v7': bonusNotesData.taxableAmount || 0,          // 課税対象額
    'bn_v8': bonusNotesData.netPaymentAmount || 0,       // 差引支給額
  };
  
  
  return result;
};

/**
 * Combines all data mappings into a single object for easy lookup.
 *
 * Merges all section mappings (payment, deduction, attendance, notes) into one object
 * based on data type (salary or bonus). This allows PayslipTable to easily look up
 * any valueId regardless of which section it belongs to.
 *
 * @param dataType - Whether this is 'salary' or 'bonus' data
 * @param paymentData - Payment data (for salary)
 * @param deductionData - Deduction data (for salary)
 * @param attendanceData - Attendance data (for salary)
 * @param bonusPaymentData - Bonus payment data (for bonus)
 * @param bonusDeductionData - Bonus deduction data (for bonus)
 * @param bonusAttendanceData - Bonus attendance data (for bonus)
 * @param salaryNotesData - Salary notes data (for salary)
 * @param bonusNotesData - Bonus notes data (for bonus)
 * @returns Combined mapping of all valueIds to their values
 */
export const getAllDataMappings = (
  dataType: 'salary' | 'bonus',
  paymentData: PaymentData | null,
  deductionData: DeductionData | null,
  attendanceData: AttendanceData | null,
  bonusPaymentData: BonusPaymentData | null,
  bonusDeductionData: BonusDeductionData | null,
  bonusAttendanceData: BonusAttendanceData | null,
  salaryNotesData: SalaryNotesData | null,
  bonusNotesData: BonusNotesData | null
): Record<string, number | string> => {
  if (dataType === 'salary') {
    return {
      ...mapPaymentDataToCells(paymentData),
      ...mapDeductionDataToCells(deductionData),
      ...mapAttendanceDataToCells(attendanceData),
      ...mapSalaryNotesDataToCells(salaryNotesData),
    };
  } else {
    return {
      ...mapBonusPaymentDataToCells(bonusPaymentData),
      ...mapBonusDeductionDataToCells(bonusDeductionData),
      ...mapBonusAttendanceDataToCells(bonusAttendanceData),
      ...mapBonusNotesDataToCells(bonusNotesData),
    };
  }
};

/**
 * Formats a number value for display in the payslip.
 *
 * Ensures numbers display as "506,000" instead of "50万6000".
 * Uses 'en-US' locale for consistent display across all devices,
 * regardless of browser locale settings.
 *
 * @param value - The numeric value to format (number or string)
 * @returns Formatted string with proper number formatting (standard format with commas, not Japanese notation)
 *          Returns empty string for zero, null, or undefined values
 */
export const formatPayslipValue = (value: number | string): string => {
  // Handle string values
  if (typeof value === 'string') {
    // Return empty string for string zeros, empty strings, and time format zeros
    if (value === '0' || value === '' || value.trim() === '' || value === '0:00') return '';
    // Return string as-is (for time strings like "8:00")
    return value;
  }
  
  // Return empty string for zero, null, or undefined numeric values
  if (value === 0 || value === null || value === undefined) return '';
  
  /**
   * Format numbers with commas for thousands using standard format (not Japanese notation).
   * Use 'en-US' locale to ensure standard number format (506,000) instead of Japanese format (50万6000).
   * This ensures consistent display across all devices regardless of browser locale.
   */
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useGrouping: true
  });
};
